/**
 * localStorage wrapper with namespaced keys + in-memory fallback for
 * browsers that throw on quota exceeded / private mode.
 *
 * Keys are documented in signage-web-player-spec §11.1.
 */

export const STORAGE_KEYS = {
  deviceCode: 'alora.signage.device_code',
  token: 'alora.signage.token',
  config: 'alora.signage.cache.config',
  session: 'alora.signage.cache.session',
  org: 'alora.signage.cache.org',
  lastSuccessAt: 'alora.signage.cache.last_success_at',
} as const;

export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

const DEVICE_CODE_PREFIX = 'web-';
const DEVICE_CODE_RANDOM_HEX_LENGTH = 24;
const DEVICE_CODE_MIN_LENGTH = 8;
const DEVICE_CODE_MAX_LENGTH = 32;

/**
 * A drop-in Storage implementation that falls back to an in-memory Map
 * when the real localStorage throws. Matches enough of the Storage
 * interface that the rest of the player code never has to care.
 */
export interface PlayerStorage {
  get(key: StorageKey): string | null;
  set(key: StorageKey, value: string): void;
  remove(key: StorageKey): void;
  /** Remove every namespaced key; used on unpair + 7-day wipe. */
  wipe(): void;
  /** True when the storage survives a reload (localStorage reachable). */
  readonly persistent: boolean;
}

export function createStorage(backing?: Storage): PlayerStorage {
  const memory = new Map<string, string>();
  const probe = backing ?? (typeof localStorage === 'undefined' ? undefined : localStorage);
  const localAvailable = (() => {
    if (!probe) return false;
    try {
      const k = 'alora.signage.__probe__';
      probe.setItem(k, '1');
      probe.removeItem(k);
      return true;
    } catch {
      return false;
    }
  })();

  const usesLocal = localAvailable && probe !== undefined;

  return {
    persistent: usesLocal,

    get(key) {
      if (usesLocal) {
        try {
          return probe!.getItem(key);
        } catch {
          // Mid-session eviction: fall through to memory.
        }
      }
      return memory.get(key) ?? null;
    },

    set(key, value) {
      memory.set(key, value);
      if (usesLocal) {
        try {
          probe!.setItem(key, value);
        } catch {
          // Quota exceeded — keep memory copy, spec §11.3 fallback.
        }
      }
    },

    remove(key) {
      memory.delete(key);
      if (usesLocal) {
        try {
          probe!.removeItem(key);
        } catch {
          /* swallow */
        }
      }
    },

    wipe() {
      for (const key of Object.values(STORAGE_KEYS)) {
        memory.delete(key);
        if (usesLocal) {
          try {
            probe!.removeItem(key);
          } catch {
            /* swallow */
          }
        }
      }
    },
  };
}

/** Generate a browser-stable device_code; see spec §2.1. */
export function ensureDeviceCode(storage: PlayerStorage): string {
  const existing = storage.get(STORAGE_KEYS.deviceCode);
  if (existing && isApiCompatibleDeviceCode(existing)) return existing;
  const fresh = `${DEVICE_CODE_PREFIX}${randomHex(DEVICE_CODE_RANDOM_HEX_LENGTH)}`;
  storage.set(STORAGE_KEYS.deviceCode, fresh);
  return fresh;
}

function isApiCompatibleDeviceCode(value: string): boolean {
  return value.length >= DEVICE_CODE_MIN_LENGTH && value.length <= DEVICE_CODE_MAX_LENGTH;
}

function randomHex(length: number): string {
  const c: Crypto | undefined =
    typeof globalThis.crypto === 'undefined' ? undefined : globalThis.crypto;
  if (c?.getRandomValues) {
    const bytes = new Uint8Array(Math.ceil(length / 2));
    c.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('').slice(0, length);
  }
  // Last-ditch non-crypto RNG — only reached on truly ancient browsers;
  // the browser feature-probe in main.ts will have already rejected them.
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`
    .replace(/[^a-z0-9]/g, '')
    .padEnd(length, '0')
    .slice(0, length);
}
